<template>
  <LoaderOverlay :active="isLoading" />

  <div v-if="!isLoading">
    <BaseAccount
      v-if="appointments.length"
      :title="$t('Upcoming Appointments')"
      :appointments="appointments"
    >
      <template v-slot:doctorSlot="doctorProps">
        <DoctorComponent
          :doctor="doctorProps.doctor"
          :showServiceTime="true"
          :showMapButton="hasMapLocation(doctorProps.doctor)"
          :showCancelButton="doctorProps.doctor?.canCancel === true"
          :show-payment-method="false"
          :show-doctor-reviews="false"
          @cancel-appointment="() => openCancelModal(doctorProps.appointment)"
          @view-map="openMapModal"
        />
      </template>
    </BaseAccount>

    <AccountEmptyState
      v-else
      :title="$t('Upcoming Appointments')"
      :message="$t('No results found')"
      :description="$t('You currently do not have any upcoming appointments.')"
      :button-label="$t('Find a specialist')"
      :button-to="'/search'"
    />
  </div>

  <CancelPopup
    v-if="isCancelModalVisible && selectedAppointment"
    id="cancelQuotePopup"
    :doctor="selectedAppointment.doctor"
    :showServiceTime="true"
    :showCancelAppointmentButton="true"
    :loading="isCancelling"
    :error-message="cancelError"
    @close="closeCancelModal"
    @confirm="confirmCancel"
  />

  <MapModal
    v-if="isMapModalVisible"
    :doctors="mapDoctors"
    :map-only="true"
    @close="closeMapModal"
  />
</template>

<script>
import { defineComponent } from "vue";
import DoctorComponent from "@/components/doctor/doctorComponent.vue";
import CancelPopup from "@/components/doctor/cancelPopup.vue";
import BaseAccount from "@/components/account/baseAccount.vue";
import AccountEmptyState from "@/components/account/AccountEmptyState.vue";
import MapModal from "@/components/search/mapModal.vue";
import LoaderOverlay from "@/components/loader/loader.vue";
import CustomerHelper from "@/helpers/customerHelper";
import { customerBookings } from "@/services/naaviApi/internalService";
import { cancelBooking } from "@/services/medkusApi/publicBookingsService";
import { mapCustomerBookingsToAppointments } from "@/helpers/customerBookingsMapper";

export default defineComponent({
  name: "UpcomingAppointmentsView",
  components: {
    DoctorComponent,
    CancelPopup,
    MapModal,
    BaseAccount,
    AccountEmptyState,
    LoaderOverlay,
  },
  data() {
    return {
      appointments: [],
      selectedAppointment: null,
      isCancelModalVisible: false,
      isMapModalVisible: false,
      mapDoctors: [],
      isLoading: false,
      isCancelling: false,
      cancelError: "",
    };
  },
  mounted() {
    this.loadAppointments();
  },
  methods: {
    hasMapLocation(doctor) {
      return Boolean(
        doctor &&
          doctor.position &&
          doctor.position.lat != null &&
          doctor.position.lng != null,
      );
    },
    openCancelModal(appointment) {
      if (!appointment) {
        return;
      }
      this.selectedAppointment = appointment;
      this.cancelError = "";
      this.isCancelling = false;
      this.isCancelModalVisible = true;
    },
    closeCancelModal() {
      this.isCancelModalVisible = false;
      this.isCancelling = false;
      this.cancelError = "";
      this.selectedAppointment = null;
    },
    openMapModal(doctor) {
      if (!doctor) {
        this.mapDoctors = [];
        this.isMapModalVisible = false;
        return;
      }
      this.mapDoctors = [doctor];
      this.isMapModalVisible = true;
    },
    closeMapModal() {
      this.isMapModalVisible = false;
      this.mapDoctors = [];
    },
    removeAppointment(bookingId) {
      if (bookingId === null || bookingId === undefined) {
        return;
      }
      this.appointments = this.appointments.filter(
        (appointment) => appointment && appointment.id !== bookingId,
      );
    },
    async loadAppointments() {
      const user = CustomerHelper.getUser();

      if (!user) {
        this.appointments = [];
        return;
      }

      this.isLoading = true;

      try {
        const response = await customerBookings();
        const upcoming = response && response.upcoming ? response.upcoming : [];
        this.appointments = mapCustomerBookingsToAppointments(upcoming);
      } catch (error) {
        this.appointments = [];
      } finally {
        this.isLoading = false;
      }
    },
    async confirmCancel() {
      if (this.isCancelling || !this.selectedAppointment) {
        return;
      }

      const user = CustomerHelper.getUser();
      if (!user) {
        this.cancelError = this.$t("Unable to cancel appointment");
        return;
      }

      const selectedDoctor =
        this.selectedAppointment && this.selectedAppointment.doctor
          ? this.selectedAppointment.doctor
          : null;
      const tenantUrl =
        selectedDoctor && selectedDoctor.tenantUrl
          ? selectedDoctor.tenantUrl
          : null;
      if (!tenantUrl) {
        this.cancelError = this.$t("Unable to cancel appointment");
        return;
      }

      this.cancelError = "";
      this.isCancelling = true;

      try {
        const bookingId = this.selectedAppointment.id;
        const response = await cancelBooking(bookingId, 0, { tenantUrl });
        const data = response && response.data ? response.data : response;

        if (!data || data.ok === false) {
          this.cancelError =
            data?.errorMsg || String(this.$t("Unable to cancel appointment"));
          return;
        }

        this.removeAppointment(bookingId);
        this.closeCancelModal();
      } catch (error) {
        this.cancelError = String(this.$t("Unable to cancel appointment"));
      } finally {
        this.isCancelling = false;
      }
    },
  },
});
</script>

<style scoped></style>
